/**
 * Clearcut area (ha) per region/sensor/year, served from a precomputed JSON file.
 *
 * The JSON at /data/clearcut_stats.json is generated from the classified rasters
 * by compute_clearcut_stats_cog.py in boreal-canada-mapping -- run it through
 * `node scripts/stats/refresh-stats.js clearcut` (also runs after every COG
 * upload). Shape, plus _entering/_carried/_window/_accuracy series per region:
 *   { "wabigoon_hls": { "2010": ha, … },         // accumulated area per year
 *     "wabigoon_hls_annual": { "2015": ha, … } }  // new-pixel-only area per year
 *
 * Cache key: `${region}_${sensor}` — same convention as the old in-browser computation.
 */


// Sensor used when a module/year doesn't specify one. Single source of truth
// so adding a future sensor means changing this (or wiring a selector back in)
// rather than hunting down hardcoded 'hls' literals.
export const DEFAULT_CLEARCUT_SENSOR = 'hls';

// Years whose tiles live under a sensor subfolder (hls/, or a future sensor's)
export const CLEARCUT_SENSOR_SUBFOLDER_YEARS = [2025];

let _statsPromise = null;

function loadStats() {
  if (!_statsPromise) {
    // Always fetch from the app's own origin (Vercel build output), not the tile CDN.
    // process.env.PUBLIC_URL is set by CRA to the correct root for public/ assets.
    const base = process.env.PUBLIC_URL || '';
    _statsPromise = fetch(`${base}/data/clearcut_stats.json`)
      .then(r => {
        if (!r.ok) throw new Error(`clearcut_stats.json: HTTP ${r.status}`);
        return r.json();
      })
      .catch(err => {
        console.error('[ClearcutStats]', err);
        _statsPromise = null;
        throw err;
      });
  }
  return _statsPromise;
}

/**
 * Returns clearcut area in hectares for each year.
 *
 * @param {string} region
 * @param {number[]} years
 * @param {Function|null} onProgress - optional callback(year, areaHa) per year
 * @param {string} sensor - e.g. 'hls'
 * @returns {Promise<Object>} year → areaHa
 */
export async function computeClearcutAreaPerYear(region, years, onProgress, sensor = DEFAULT_CLEARCUT_SENSOR) {
  const all = await loadStats();
  const cacheKey = `${region}_${sensor}`;
  const byYear = all[cacheKey] ?? {};

  const results = {};
  for (const year of years) {
    const ha = byYear[String(year)] ?? 0;
    results[year] = ha;
    if (onProgress) onProgress(year, ha);
  }
  return results;
}

/** Returns annual (new-pixel-only) clearcut area in hectares for each year. */
export async function computeAnnualClearcutAreaPerYear(region, years, sensor = DEFAULT_CLEARCUT_SENSOR) {
  const all = await loadStats();
  const byYear = all[`${region}_${sensor}_annual`] ?? {};
  const results = {};
  for (const year of years) {
    results[year] = byYear[String(year)] ?? 0;
  }
  return results;
}

/**
 * Newly standing clearcut per year: in accumulated(Y) but not accumulated(Y-1).
 *
 * Distinct from the `_annual` series, which is the raw classification -- every
 * pixel that looked cut in Y, most of which were already standing from earlier
 * years. Only `entering` answers "how much was newly cut", and only
 * `entering + carried` adds up to accumulated, so only these two can be stacked
 * against it honestly.
 *
 * Returns {} for regions whose stats predate the field; callers fall back to
 * the old accumulated-minus-annual split.
 */
export async function computeEnteringClearcutAreaPerYear(region, years, sensor = DEFAULT_CLEARCUT_SENSOR) {
  const all = await loadStats();
  const byYear = all[`${region}_${sensor}_entering`];
  if (!byYear) return null;
  const results = {};
  for (const year of years) results[year] = byYear[String(year)] ?? 0;
  return results;
}

/** Clearcut standing in Y that was already standing in Y-1. */
export async function computeCarriedClearcutAreaPerYear(region, years, sensor = DEFAULT_CLEARCUT_SENSOR) {
  const all = await loadStats();
  const byYear = all[`${region}_${sensor}_carried`];
  if (!byYear) return null;
  const results = {};
  for (const year of years) results[year] = byYear[String(year)] ?? 0;
  return results;
}

/**
 * Returns the set of years that have actual annual detection data for a
 * region/sensor combination (i.e. have an entry in the stats JSON).
 * Used to constrain trendlines to years where tiles were actually processed.
 */
export async function getAnnualYearsWithData(region, sensor = DEFAULT_CLEARCUT_SENSOR) {
  const all = await loadStats();
  const byYear = all[`${region}_${sensor}_annual`] ?? {};
  return new Set(Object.keys(byYear).map(Number));
}

/**
 * Returns per-year accuracy metrics (precision, recall, f1, iou) for clearcut detection.
 * Derived from validation set evaluation in the training notebooks.
 * Returns null for a year if no validation was run.
 *
 * @param {string} region
 * @param {string} sensor
 * @returns {Promise<Object>} year → { precision, recall, f1, iou } | null
 */
export async function getClearcutAccuracy(region, sensor = DEFAULT_CLEARCUT_SENSOR) {
  const all = await loadStats();
  return all[`${region}_${sensor}_accuracy`] ?? {};
}

/**
 * Per-year window metadata for the accumulated series.
 *
 * accumulated(Y) unions a 5-year window, so a year near the start of the record
 * unions fewer years than a mature one and reads lower for that reason alone.
 * Without this the rise through the early years looks like accelerating harvest
 * rather than the window filling up, which is the single easiest way to misread
 * this chart.
 *
 * Shape per year:
 *   { observationYears, expectedYears, sourceYears, carriesBaseline,
 *     isBaseline, comparable }
 *
 * Returns {} for regions whose stats predate this field, so callers should treat
 * "no metadata" as "no annotation" rather than as "not comparable".
 */
export async function getClearcutWindowMeta(region, sensor = DEFAULT_CLEARCUT_SENSOR) {
  const all = await loadStats();
  return all[`${region}_${sensor}_window`] ?? {};
}

/**
 * Regions that have any clearcut product at all, for a sensor.
 *
 * The stats file is written by the same pipeline that generates the tiles and
 * COGs, so a region absent from it has no clearcut raster to request. Without
 * this the map fell back to a PNG pyramid for every selected region -- at 39
 * FMUs that is thousands of tile requests, all 404, for regions that were never
 * processed.
 */
export async function getRegionsWithClearcutData(sensor = DEFAULT_CLEARCUT_SENSOR) {
  const all = await loadStats();
  const suffix = `_${sensor}`;
  return new Set(
    Object.keys(all)
      // `<region>_<sensor>` is the accumulated series; the _annual, _window and
      // _accuracy keys are derived from it, so matching the bare key is enough.
      .filter((key) => key.endsWith(suffix))
      .map((key) => key.slice(0, -suffix.length)),
  );
}

export function clearStatsCache() {
  _statsPromise = null;
}
